import { ProposalRepository } from "./ProposalRepository";
import { SavedProposal } from "../types/proposal";

const STORAGE_KEY_PREFIX = "noar-health:commercial-proposals:v1";
const STORAGE_VERSION = 1;

type StoredProposalCollection = {
  version: number;
  proposals: SavedProposal[];
};

const isSavedProposal = (value: unknown): value is SavedProposal => {
  if (!value || typeof value !== "object") return false;
  const proposal = value as Partial<SavedProposal>;
  return !!(
    proposal.id &&
    proposal.status &&
    proposal.createdAt &&
    proposal.updatedAt &&
    proposal.draft?.client &&
    Array.isArray(proposal.draft?.products) &&
    proposal.totals
  );
};

export class LocalStorageProposalRepository implements ProposalRepository {
  private readonly storageKey: string;

  constructor(ownerId: string) {
    const safeOwnerId = ownerId.replace(/[^a-zA-Z0-9_-]/g, "") || "admin";
    this.storageKey = `${STORAGE_KEY_PREFIX}:${safeOwnerId}`;
  }

  private read(): SavedProposal[] {
    const storedValue = window.localStorage.getItem(this.storageKey);
    if (!storedValue) return [];

    try {
      const parsed = JSON.parse(storedValue) as Partial<StoredProposalCollection>;
      if (parsed.version !== STORAGE_VERSION || !Array.isArray(parsed.proposals)) {
        return [];
      }

      return parsed.proposals.filter(isSavedProposal);
    } catch {
      return [];
    }
  }

  private write(proposals: SavedProposal[]): void {
    const collection: StoredProposalCollection = {
      version: STORAGE_VERSION,
      proposals,
    };
    window.localStorage.setItem(this.storageKey, JSON.stringify(collection));
  }

  async list(): Promise<SavedProposal[]> {
    return this.read().sort(
      (first, second) =>
        new Date(second.updatedAt).getTime() - new Date(first.updatedAt).getTime(),
    );
  }

  async save(proposal: SavedProposal): Promise<SavedProposal> {
    const proposals = this.read();
    const existingIndex = proposals.findIndex(item => item.id === proposal.id);
    if (existingIndex >= 0) proposals[existingIndex] = proposal;
    else proposals.push(proposal);
    this.write(proposals);
    return proposal;
  }

  async remove(proposalId: string): Promise<void> {
    this.write(this.read().filter(proposal => proposal.id !== proposalId));
  }
}
